import { Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { ROUTES } from './routes';
import { AppShell } from '@/components/layout/AppShell';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { PublicOnlyRoute } from '@/components/common/PublicOnlyRoute';
import { AdminRoute } from '@/components/common/AdminRoute';
import { Spinner } from '@/components/ui/Spinner';
import { lazyWithReload as lazy } from '@/lib/lazyWithReload';

const LoginPage = lazy(() =>
  import('@/pages/auth/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const RegisterPage = lazy(() =>
  import('@/pages/auth/RegisterPage').then((m) => ({ default: m.RegisterPage })),
);
const VerifyEmailPage = lazy(() =>
  import('@/pages/auth/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('@/pages/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const NotFoundPage = lazy(() =>
  import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);
const DashboardPage = lazy(() =>
  import('@/pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const ActivitiesPage = lazy(() =>
  import('@/pages/activities/ActivitiesPage').then((m) => ({ default: m.ActivitiesPage })),
);
const ProfilePage = lazy(() =>
  import('@/pages/profile/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const SettingsPage = lazy(() =>
  import('@/pages/profile/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const AchievementsPage = lazy(() =>
  import('@/pages/profile/AchievementsPage').then((m) => ({ default: m.AchievementsPage })),
);
const NotificationsPage = lazy(() =>
  import('@/pages/profile/NotificationsPage').then((m) => ({ default: m.NotificationsPage })),
);
const PublicProfilePage = lazy(() =>
  import('@/pages/public/PublicProfilePage').then((m) => ({ default: m.PublicProfilePage })),
);
const FriendsPage = lazy(() =>
  import('@/pages/friends/FriendsPage').then((m) => ({ default: m.FriendsPage })),
);
const CommunitiesIndexPage = lazy(() =>
  import('@/pages/communities/CommunitiesIndexPage').then((m) => ({
    default: m.CommunitiesIndexPage,
  })),
);
const CommunityPage = lazy(() =>
  import('@/pages/communities/CommunityPage').then((m) => ({ default: m.CommunityPage })),
);
const LeaderboardsPage = lazy(() =>
  import('@/pages/leaderboards/LeaderboardsPage').then((m) => ({ default: m.LeaderboardsPage })),
);
const ChallengesPage = lazy(() =>
  import('@/pages/challenges/ChallengesPage').then((m) => ({ default: m.ChallengesPage })),
);
const AdminChallengesPage = lazy(() =>
  import('@/pages/admin/AdminChallengesPage').then((m) => ({ default: m.AdminChallengesPage })),
);

function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <Spinner className="h-6 w-6 text-parchment-ink/60" />
    </div>
  );
}

export function AppRouter() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route element={<PublicOnlyRoute />}>
          <Route path={ROUTES.login} element={<LoginPage />} />
          <Route path={ROUTES.register} element={<RegisterPage />} />
          <Route path={ROUTES.forgotPassword} element={<ForgotPasswordPage />} />
          <Route path={ROUTES.resetPassword()} element={<ResetPasswordPage />} />
        </Route>

        <Route path={ROUTES.verifyEmail()} element={<VerifyEmailPage />} />
        <Route path={ROUTES.publicProfile()} element={<PublicProfilePage />} />

        <Route element={<ProtectedRoute />}>
          <Route element={<AppShell />}>
            <Route path={ROUTES.dashboard} element={<DashboardPage />} />
            <Route path={ROUTES.activities} element={<ActivitiesPage />} />
            <Route path={ROUTES.friends} element={<FriendsPage />} />
            <Route path={ROUTES.communities} element={<CommunitiesIndexPage />} />
            <Route path={ROUTES.community()} element={<CommunityPage />} />
            <Route path={ROUTES.leaderboards} element={<LeaderboardsPage />} />
            <Route path={ROUTES.challenges} element={<ChallengesPage />} />
            <Route path={ROUTES.profile} element={<ProfilePage />} />
            <Route path={ROUTES.settings} element={<SettingsPage />} />
            <Route path={ROUTES.achievements} element={<AchievementsPage />} />
            <Route path={ROUTES.notifications} element={<NotificationsPage />} />
            <Route element={<AdminRoute />}>
              <Route path={ROUTES.adminChallenges} element={<AdminChallengesPage />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
